import type { ConsentRequest, CreateSessionRequest, CreateSessionResponse, SessionVideoStatusView } from "@fiket/booth-schema";
import { unwrapEnvelope } from "../../lib/apiEnvelope";

/**
 * 세션 발급 `POST /api/sessions` 클라이언트 (BOOTH-74 에서 PrintShareView 밖으로).
 * 이 라우트는 레거시 평면 `{ error, message }` 와 봉투 `{ success:false, error:{ code, message } }` 두 형태를
 * 모두 돌려줄 수 있다(결제 게이트 402 PAYMENT_REQUIRED · 409 PAYMENT_ALREADY_USED 는 봉투) — 둘 다 파싱한다.
 * 성공 응답은 봉투 없이 `CreateSessionResponse` 평면 그대로.
 */
const FRAME_ID = "single";

export class SessionApiError extends Error {
  readonly code: string;
  readonly httpStatus: number;
  constructor(code: string, message: string, httpStatus: number) {
    super(message);
    this.name = "SessionApiError";
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

export function parseSessionErrorBody(httpStatus: number, body: unknown): { code: string; message: string } {
  if (body && typeof body === "object") {
    const b = body as { success?: unknown; error?: unknown; message?: unknown };
    if (b.success === false && b.error && typeof b.error === "object") {
      const e = b.error as { code?: unknown; message?: unknown };
      if (typeof e.code === "string") return { code: e.code, message: typeof e.message === "string" ? e.message : e.code };
    }
    if (typeof b.error === "string") return { code: b.error, message: typeof b.message === "string" ? b.message : b.error };
  }
  return { code: "HTTP_ERROR", message: `session create ${httpStatus}` };
}

/** 동의 인자 타입 — consentId 우선, 없으면 consent 인라인, 둘 다 없으면 키 없음 (BOOTH-66) */
export interface ConsentArg {
  consentId?: string | null;
  consent?: ConsentRequest | null;
}

/**
 * 세션 생성 본문을 조립하는 순수 함수 (BOOTH-66).
 * consentId 가 truthy 면 consentId 만, 없고 consent 가 truthy 면 consent 만, 둘 다 없으면 키 없음.
 */
export function buildSessionBody(
  boothId: string,
  paymentId: string | null,
  videoJobId: string | null,
  meta: Pick<CreateSessionRequest, "themeId" | "styleId" | "language" | "visitId">,
  consentArg: ConsentArg = {}
): CreateSessionRequest {
  const consentFields: Partial<CreateSessionRequest> = consentArg.consentId
    ? { consentId: consentArg.consentId }
    : consentArg.consent
    ? { consent: consentArg.consent }
    : {};
  return {
    boothId,
    frameId: FRAME_ID,
    filterId: null,
    ...(paymentId ? { paymentId } : {}),
    ...(videoJobId ? { videoJobId } : {}),
    ...(meta.themeId ? { themeId: meta.themeId } : {}),
    ...(meta.styleId ? { styleId: meta.styleId } : {}),
    ...(meta.language ? { language: meta.language } : {}),
    ...(meta.visitId ? { visitId: meta.visitId } : {}),
    ...consentFields,
  };
}

/**
 * 결제 유효 ON 부스는 paid 결제의 paymentId 가 필수(서버 게이트). 없으면 보내지 않는다(결제 OFF 부스).
 * `videoJobId` 는 진행 중/완료된 영상 job (BOOTH-60 지연 전달) — 서버가 세션에 붙이고 완료 시 자동 연결한다. 없으면 보내지 않는다.
 */
export async function createSession(
  boothId: string,
  paymentId: string | null,
  videoJobId: string | null = null,
  /** 통계 축 (BOOTH-87) — 테마·스타일·언어·방문 id. 비어 있는 값은 보내지 않는다. */
  meta: Pick<CreateSessionRequest, "themeId" | "styleId" | "language" | "visitId"> = {},
  /** 개인정보 동의 (BOOTH-66) — consentId 우선, 없으면 consent 인라인. */
  consentArg: ConsentArg = {}
): Promise<CreateSessionResponse> {
  const res = await fetch("/api/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(buildSessionBody(boothId, paymentId, videoJobId, meta, consentArg)),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    const { code, message } = parseSessionErrorBody(res.status, body);
    throw new SessionApiError(code, message, res.status);
  }
  return (await res.json()) as CreateSessionResponse;
}

/** 세션 영상 상태 `GET /api/sessions/:code/video` (BOOTH-60 지연 전달) — 받기 화면이 5초마다 폴링. 봉투 응답. */
export async function fetchSessionVideo(shortCode: string, signal: AbortSignal): Promise<SessionVideoStatusView> {
  const res = await fetch(`/api/sessions/${encodeURIComponent(shortCode)}/video`, { signal, cache: "no-store" });
  return unwrapEnvelope<SessionVideoStatusView>(res);
}
